// @ts-nocheck
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableRow,
    LoadingOverlay,
    Pagination,
    Typography,
} from "@insite/mobius/index";

import translate from "@insite/client-framework/Translate";
import getColor from "@insite/mobius/utilities/getColor";
import EditProductsBuyingsListButton from "@store/Library/Components/Bunzl/Common/EditItemListButton";
import React, { useEffect } from "react";

import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { TypographyProps } from "@insite/mobius/Typography";
import useBuyingList from "@store/Framework/Common/Hooks/useBuyingList/useBuyingList";
import useFetchWhenUpdateBuyingListParams from "@store/Framework/Common/Hooks/useBuyingList/useUpdateBuyingListPagination";
import useIsAdmin from "@store/Framework/Common/Hooks/useIsAdmin";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import EditUsersBuyingsListButton from "@store/Library/Components/Bunzl/Common/EditUsersButton";
import BuyingListTableHead from "@store/Library/Components/BuyingList/BuyingListTableHead";
import ManageProductsModal from "@store/Library/Components/BuyingList/ManageProductsModal";
import ManageUsersModal from "@store/Library/Components/BuyingList/ManageUsersModal/ManageUsersModal";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import styled, { css } from "styled-components";
import BuyingListDetailsLink from "../../Components/BuyingList/BuyingListDetailsLink";

const desktopQuery = getMediaQuery({ option: "up", first: false, min: 700, max: 5000, last: true });
const mobileQuery = getMediaQuery({ option: "down", first: true, min: 0, max: 700, last: false });
const userWidths = [40, 40, 20];
const mobileAdminWidths = [50, 50];
const adminWidths = [30, 30, 20, 20];
export const StyledCol = styled.col<{ width: number }>`
    width: ${({ width }) => width}%;
`;

interface BuyingListManagementTableProps extends WidgetProps {}

export interface BuyingListManagementTableStyles {
    dataTable: DataTableProps;
    dataTableRow: DataTableRowProps;
    buyingListLinkCell: DataTableCellProps;
    buyingListDescriptionCell: DataTableCellProps;
    productsCell: DataTableCellProps;
    nonAdminProductsTypography: TypographyProps;
}

export const buyingListManagementTableStyles: BuyingListManagementTableStyles = {
    dataTable: {
        css: css`
            table-layout: fixed;
            width: 100%;
            ${desktopQuery} {
                min-width: 50rem;
            }
        `,
    },
    dataTableRow: {
        css: css`
            transition: all 0.2s ease-in-out;
            border-radius: 0.5rem;
        `,
    },
    buyingListLinkCell: {
        typographyProps: {
            ellipsis: true,

            underline: true,
            css: css`
                display: block;
                max-width: 10rem;
                font-size: ${({ theme }) => theme.design.typography.size.smaller};
            `,
        },
        css: css`
            span {
                ${mobileQuery} {
                    max-width: 8rem;
                }
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
                max-width: 12rem;
            }
            font-size: 1rem;
            font-weight: 600;
        `,
    },
    buyingListDescriptionCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 14rem;
                display: block;
                font-size: ${({ theme }) => theme.design.typography.size.smaller};

                ${mobileQuery} {
                    width: 8rem;
                }
            `,
        },
    },
    productsCell: {
        css: css`
            gap: 0.5rem;
            padding-left: 2rem;
        `,
    },
    nonAdminProductsTypography: {
        css: css`
            font-size: 1rem;
            color: ${getColor("primary")};
        `,
    },
};
const buyingListAdminHeaders = [translate("Name"), translate("Description"), translate("Users"), translate("Products")];

const buyingListUserHeaders = [translate("Name"), translate("Description"), translate("Products")];

const BuyingListManagementTable: React.FC<BuyingListManagementTableProps> = () => {
    const [openProductModalId, setOpenProductModalId] = React.useState<string | null>(null);
    const { getBuyingLists, buyingLists: listCollection, setPage, loading } = useBuyingList();
    const [openUserModalId, setOpenUserModalId] = React.useState<string | null>(null);
    const isMobile = useIsMobile();
    const isAdmin = useIsAdmin();
    const buyingListHeaders = isAdmin ? buyingListAdminHeaders : buyingListUserHeaders;
    useFetchWhenUpdateBuyingListParams();
    useEffect(() => {
        getBuyingLists(true);
    }, []);
    const buyingLists = listCollection?.wishListCollection || [];

    const handleClose = () => {
        setOpenProductModalId(null);
        setOpenUserModalId(null);
        getBuyingLists(true);
    };

    return (
        <LoadingOverlay loading={loading} data-test-selector="buyingListManagement_loadingOverlay">
            {buyingLists && (
                <DataTable
                    {...buyingListManagementTableStyles.dataTable}
                    data-test-selector="buyingListManagement_dataTable"
                >
                    <colgroup>
                        {isAdmin ? (
                            <>
                                {isMobile ? (
                                    <>
                                        {mobileAdminWidths.map((width, index) => (
                                            <StyledCol width={width} key={`${width}${index.toString()}`} />
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        {adminWidths.map((width, index) => (
                                            <StyledCol width={width} key={`${width}${index.toString()}}`} />
                                        ))}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {userWidths.map((width, index) => (
                                    <StyledCol width={width} key={`${width}${index.toString()}`} />
                                ))}
                            </>
                        )}
                    </colgroup>

                    <BuyingListTableHead
                        buyingListHeaders={isMobile ? buyingListHeaders.slice(0, 2) : buyingListHeaders}
                    />

                    <DataTableBody data-test-selector="buyingListManagement_tableBody">
                        {buyingLists?.map(list => {
                            const numberOfUsers: number | string = list?.properties?.buyingListUserCount || 0;
                            const numberOfProducts: number | string = list?.properties?.wishListLineCount || 0;
                            return (
                                <DataTableRow
                                    key={list.id}
                                    {...buyingListManagementTableStyles.dataTableRow}
                                    data-test-selector={`buyingListManagement_tableRow_${list.id}`}
                                >
                                    <DataTableCell
                                        title={list.name}
                                        {...buyingListManagementTableStyles.buyingListLinkCell}
                                        css={css`
                                            span {
                                                ${mobileQuery} {
                                                    max-width: 8rem;
                                                }
                                                text-overflow: ellipsis;
                                                overflow: hidden;
                                                white-space: nowrap;
                                                max-width: ${isAdmin ? "14rem" : "20rem"};
                                            }
                                        `}
                                        data-test-selector="buyingListManagement_nameCell"
                                    >
                                        <BuyingListDetailsLink name={list?.name} id={list?.id} />
                                    </DataTableCell>

                                    <DataTableCell
                                        {...buyingListManagementTableStyles.buyingListDescriptionCell}
                                        data-test-selector="buyingListManagement_descriptionCell"
                                    >
                                        {list.description}
                                    </DataTableCell>
                                    {isAdmin && !isMobile && (
                                        <DataTableCell data-test-selector="buyingListManagement_usersCell">
                                            <EditUsersBuyingsListButton
                                                onClick={() => setOpenUserModalId(list.id)}
                                                numberOfUsers={numberOfUsers}
                                            />
                                        </DataTableCell>
                                    )}
                                    <DataTableCell
                                        {...buyingListManagementTableStyles.productsCell}
                                        data-test-selector="buyingListManagement_productsCell"
                                    >
                                        {isAdmin ? (
                                            !isMobile && (
                                                <>
                                                    <ManageProductsModal
                                                        buyingList={list}
                                                        isOpen={openProductModalId === list.id}
                                                        onClose={handleClose}
                                                        data-test-selector="buyingListManagement_manageProductsModal"
                                                    />
                                                    <EditProductsBuyingsListButton
                                                        items={Number(numberOfProducts)}
                                                        onClick={() => setOpenProductModalId(list.id)}
                                                    />
                                                </>
                                            )
                                        ) : (
                                            <Typography
                                                {...buyingListManagementTableStyles.nonAdminProductsTypography}
                                                data-test-selector="buyingListManagement_productCount"
                                            >
                                                {numberOfProducts}
                                            </Typography>
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            )}
            <Pagination
                // this prop does exist its just not typed properly
                // @ts-ignore
                hidePerPageSelect
                currentPage={listCollection?.pagination?.currentPage || 1}
                onChangePage={setPage}
                resultsCount={listCollection?.pagination?.totalItemCount || 0}
                resultsPerPage={listCollection?.pagination?.pageSize || 0}
                onChangeResultsPerPage={() => {}}
                resultsPerPageOptions={[]}
                data-test-selector="buyingListManagement_pagination"
            />
            <ManageUsersModal
                buyingList={buyingLists.find(list => list.id === openUserModalId) || null}
                onClose={handleClose}
                key={openUserModalId || ""}
                isOpen={openUserModalId !== null}
                selectedUsers={[]}
            />
        </LoadingOverlay>
    );
};

const widgetModule: CustomWidgetModule = {
    component: BuyingListManagementTable,
    definition: {
        group: "Buying List",
        fieldDefinitions: [],
    },
};

export default widgetModule;
